import React, { useState } from 'react';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import {
    Typography,
    Button,
    makeStyles,
    Menu,
    MenuItem,
    Tooltip,
    IconButton
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import DeleteIcon from '@material-ui/icons/Delete';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import SaveOutlinedIcon from '@material-ui/icons/SaveOutlined';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import {
    PolygonIcon,
    ExtrudeIcon,
    CircleIcon,
    LineIcon,
    LocationPinIcon
} from '../../../Mapbox/Icons';

import { MAIN_MAP } from '../../../../redux/map/Constants';
import { showSecondaryPanel } from '../../../../redux/panel/Actions';
import { NEW_SKETCHES } from '../../../../redux/panel/PanelNames';
import {
    setDrawnFeatures,
    setDrawingFeature,
    setShapeFeatureProperty
} from '../../../../redux/map/Actions';

import PolygonFeatureProperties from './PolygonFeatureProperties';
import LineFeatureProperties from './LineFeatureProperties';
import PointFeatureProperties from './PointFeatureProperties';
import CreateSiteCardDialog from '../../../Common/SiteCard/CreateDialog';
import ConfirmationDialog from '../../../Common/UI/ConfirmationDialog';
import SaveSketchDialog from '../drawnsketches/newSketches/SaveSketchDialog';

const propTypes = {};

const defaultProps = {};

const useStyles = makeStyles(theme => styles(theme));

function DrawnFeatureInfoBox () {
    const classes = useStyles();
    const dispatch = useDispatch();

    const [isCreateSiteCardDialogOpen, setIsCreateSiteCardDialogOpen] = useState(false);
    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
    const [isSaveSketchOpen, setIsSaveSketchOpen] = useState(false);
    const [menuOptionsEl, setMenuOptionsEl] = useState(null);

    const drawModule = useSelector(state => state[MAIN_MAP].drawModule);
    const drawingFeature = useSelector(state => state[MAIN_MAP].drawingFeature);
    const isDrawing = useSelector(state => state[MAIN_MAP].isDrawing);
    const name = useSelector(state => state[MAIN_MAP].drawingFeature?.properties?.name);
    const saveDrawnFeatures = useSelector(
        state => state.user.features?.drawnFeatures?.saveDrawnFeatures
    );
    const editDrawnFeatures = useSelector(
        state => state.user.features?.drawnFeatures?.editDrawnFeatures
    );

    const featureIds =
        isDrawing && drawingFeature
            ? [drawingFeature.properties?.id]
            : drawModule.getDrawControl().getSelectedIds();
    const feature = drawingFeature || drawModule.getDrawControl().get(featureIds[0]);
    const isLocked = Boolean(feature?.properties?.isLocked);

    function dispatchSetDrawnFeatures (val) {
        dispatch(setDrawnFeatures(val, MAIN_MAP));
    }
    function dispatchSetDrawingFeature (val) {
        dispatch(setDrawingFeature(val, MAIN_MAP));
    }
    function dispatchShowSecondaryPanel (name) {
        dispatch(showSecondaryPanel(name));
    }
    function dispatchSetShapeFeatureProperty (userProperty, userPropertyValue) {
        dispatch(setShapeFeatureProperty(feature?.id, userProperty, userPropertyValue, MAIN_MAP));
    }

    function handleShapeLocking () {
        dispatchSetShapeFeatureProperty('isLocked', !isLocked);
    }

    function toggleConfirmationDialog () {
        setIsConfirmationDialogOpen(p => !p);
    }

    function handleCloseConfirmationDialog () {
        setIsConfirmationDialogOpen(false);
    }

    function confirmUnlockSketch () {
        toggleConfirmationDialog();
    }

    function createInfoBoxContent (feature) {
        // if (feature.properties.radius) {
        //     return <CircleFeatureProperties feature={feature}/>
        if (feature?.geometry?.type === 'Polygon') {
            return <PolygonFeatureProperties feature={feature} />;
        } else if (feature?.geometry?.type === 'LineString') {
            return <LineFeatureProperties feature={feature} />;
        } else if (feature?.geometry?.type === 'Point') {
            return <PointFeatureProperties feature={feature} />;
        }
    }

    function createIcon (feature) {
        if (feature?.properties?.radius) {
            return <CircleIcon />;
        } else if (feature.properties.height) {
            return <ExtrudeIcon />;
        } else if (feature?.geometry?.type === 'Polygon') {
            return <PolygonIcon />;
        } else if (feature?.geometry?.type === 'LineString') {
            return <LineIcon />;
        } else if (feature?.geometry?.type === 'Point') {
            return <LocationPinIcon />;
        }
    }

    function deleteFeature (feature) {
        drawModule.getDrawControl().delete(feature?.id);
        dispatchSetDrawnFeatures(drawModule.getDrawControl().getAll());
        dispatchSetDrawingFeature(null);
    }

    if (!drawModule) {
        return null;
    }

    if (featureIds.length === 0 || _.isEmpty(drawingFeature)) {
        return null;
    }

    if (!feature || feature?.geometry?.coordinates?.length === 0) {
        return null;
    }

    return (
        <div className={classes.root}>
            <SaveSketchDialog
                open={isSaveSketchOpen}
                onClose={() => setIsSaveSketchOpen(false)}
                feature={drawingFeature}
            />
            <CreateSiteCardDialog
                open={isCreateSiteCardDialogOpen}
                onClose={() => setIsCreateSiteCardDialogOpen(false)}
                defaultName="Drawn Shape"
                featureConfig={{
                    isDrawn: true
                }}
                geometry={feature.geometry}
            />
            <ConfirmationDialog
                open={isConfirmationDialogOpen}
                onClose={handleCloseConfirmationDialog}
                titleString="Unlock and edit search area"
                contentString="Are you sure you’d like to unlock and edit the search area? You will have to search for land again and will lose these results."
                confirmationLabel="UNLOCK SHAPE"
                onConfirm={confirmUnlockSketch}
            />
            <div className={classes.container}>
                <div className={classes.title}>
                    <Typography
                        variant="subtitle1"
                        color="textPrimary"
                        className={classes.titleText}>
                        {createIcon(feature)} {name || 'Drawn Shape'}
                    </Typography>
                    {isLocked && (
                        <div className={classes.lockedDiv}>
                            <Typography
                                variant="subtitle1"
                                color="textPrimary"
                                className={classes.lockedText}>
                                {'(locked)'}
                            </Typography>
                            <div>
                                <Tooltip
                                    arrow
                                    title="The shape is locked and unmovable on the map. You can unlock the shape in the ellipsis button on this modal or by clicking the lock icon in the active layers panel"
                                    placement="top"
                                    leaveDelay={300}
                                    className={classes.toolTip}>
                                    <IconButton>
                                        <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        </div>
                    )}
                </div>
                {createInfoBoxContent(feature)}
                <div className={classes.flexGrow} />
                <div className={classes.footer}>
                    <Button
                        classes={{
                            root: classes.removeButtonRoot,
                            focusVisible: classes.removeButtonFocusVisible
                        }}
                        startIcon={<DeleteIcon />}
                        onClick={() => deleteFeature(feature)}>
                        Remove
                    </Button>
                    {saveDrawnFeatures && editDrawnFeatures && (
                        <Button
                            color="secondary"
                            variant="outlined"
                            classes={{ root: classes.formatSaveShapeButton }}
                            onClick={() => {
                                feature?.properties?.id
                                    ? dispatchShowSecondaryPanel(NEW_SKETCHES)
                                    : setIsSaveSketchOpen(true);
                            }}
                            startIcon={
                                feature?.properties?.id ? (
                                    <EditOutlinedIcon />
                                ) : (
                                    <SaveOutlinedIcon />
                                )
                            }>
                            {feature?.properties?.id ? 'Edit shape' : 'Save shape'}
                        </Button>
                    )}

                    <>
                        <Button
                            variant="outlined"
                            color="secondary"
                            classes={{ root: classes.moreVertButton }}
                            onClick={e => setMenuOptionsEl(e.currentTarget)}>
                            <MoreVertIcon />
                        </Button>
                        <Menu
                            open={Boolean(menuOptionsEl)}
                            anchorEl={menuOptionsEl}
                            onClose={() => setMenuOptionsEl(null)}>
                            {!feature?.properties?.id && saveDrawnFeatures && (
                                <MenuItem
                                    onClick={() => {
                                        setIsSaveSketchOpen(true);
                                        setMenuOptionsEl(null);
                                    }}>
                                    Save Shape
                                </MenuItem>
                            )}
                            {editDrawnFeatures && (
                                <MenuItem
                                    onClick={() => {
                                        dispatchShowSecondaryPanel(NEW_SKETCHES);
                                        setMenuOptionsEl(null);
                                    }}>
                                    Edit Shape
                                </MenuItem>
                            )}
                            {(feature?.geometry?.type === 'Polygon' ||
                                feature?.geometry?.type === 'LineString') && (
                                    <MenuItem
                                        onClick={() => {
                                            setIsCreateSiteCardDialogOpen(true);
                                            setMenuOptionsEl(null);
                                        }}>
                                        Save as site card
                                    </MenuItem>
                                )}
                            <MenuItem
                                onClick={() => {
                                    handleShapeLocking();
                                    setMenuOptionsEl(null);
                                }}>
                                {isLocked ? 'Unlock Shape' : 'Lock Shape'}
                            </MenuItem>
                        </Menu>
                    </>
                </div>
            </div>
        </div>
    );
}

const styles = theme => ({
    root: {
        [theme.breakpoints.up('xl')]: {
            // Hard coded - change
            // position: 'absolute',
            position: 'fixed',
            paddingLeft: 61,
            bottom: 20,
            right: 0,
            left: 0,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none !important'
        }
    },
    container: {
        backgroundColor: theme.palette.background.main,
        boxSizing: 'border-box',
        borderRadius: theme.constants.radius.md,
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        minWidth: 449,
        padding: 12,
        pointerEvents: 'all'
    },
    markerContainer: {
        zIndex: '0 !important',
        minWidth: '220px',
        minHeight: '145px',
        padding: '10px',
        backgroundColor: theme.palette.background.main,
        border: '1px solid ' + theme.palette.grey.variant3,
        display: 'flex',
        flexDirection: 'column'
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        padding: '0 0 10px 0',
        marginBottom: 10,
        borderBottom: '1px solid ' + theme.palette.grey.variant3
    },
    titleText: {
        display: 'flex',
        alignItems: 'center',
        columnGap: '10px'
    },
    lockedText: {
        fontStyle: 'italic',
        paddingLeft: 7
    },
    toolTip: {
        padding: 5
    },
    lockedDiv: {
        display: 'flex',
        alignItems: 'center',
        height: 18
    },
    flexGrow: {
        flexGrow: 1
    },
    contrastText: {
        color: theme.palette.primary.contrastText
    },
    pointer: {
        cursor: 'pointer'
    },
    removeButtonRoot: {
        textTransform: 'initial',
        color: theme.palette.grey.variant4,
        '&:hover': {
            color: theme.palette.grey.variant5
        },
        flexGrow: 1
    },
    removeButtonFocusVisible: {
        color: theme.palette.grey.variant5
    },
    formatSaveShapeButton: {
        margin: '0 10px 0 160px'
    },
    siteCardButton: {
        lineHeight: '24px',
        fontSize: '13px'
    },
    moreVertButton: {
        minWidth: '30px',
        padding: '0'
    },
    footer: {
        display: 'flex'
    }
});

DrawnFeatureInfoBox.propTypes = propTypes;
DrawnFeatureInfoBox.defaultProps = defaultProps;

export default DrawnFeatureInfoBox;
